import Big from 'big.js';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import {
  BaseDialogLayout,
  CustomIcons,
  CustomToolTip,
  MessageDataTable,
  PrintButton,
  WrappedMarkdown
} from 'modules';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { MessageData } from 'generated/graphql';

import { theme } from 'theme';



export type MessageDataModalProps = {
  isVisible: boolean;
  handleClose: () => void;
  data: MessageData;
  ticker: string;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: MessageDataModalProps) => {
  const content = useMemo(() => __getContent(props.data), [props.data]);
  const fiscalYear = useMemo(() => props.data.meta.find((m) => m.key === 'fiscalYear')?.value, [props]);
  const fiscalQuarter = useMemo(() => props.data.meta.find((m) => m.key === 'fiscalQuarter')?.value, [props]);
  const meetingTimestamp = useMemo(() => props.data.meta.find((m) => m.key === 'meetingTimestamp')?.value, [props]);

  return (
    <BaseDialogLayout.FC
      fullScreen
      titleIcon={<CustomIcons.LargeIcon.FC icon='COMPANYLOGOFULL' />}
      formTitle={__makeFormTitle(props, fiscalQuarter, fiscalYear)}
      handleClose={() => props.handleClose()}
      isVisible={props.isVisible}
      subTitle={meetingTimestamp ? 'For the earnings call occurring on ' + DateTime.fromISO(meetingTimestamp).toFormat('MM/dd/yyyy') : ''}
      closeButton={(
        <PrintButton.FC />
      )}
    >
      {content}
    </BaseDialogLayout.FC>
  );
};



function __makeFormTitle(props: MessageDataModalProps, fiscalQuarter: string | undefined, fiscalYear: string | undefined): React.ReactNode {
  if (props.data.type === 'earningsCall') {
    return ((fiscalQuarter && fiscalYear) ? `${props.ticker} Earnings Call: ${fiscalQuarter} ${fiscalYear}` : 'Earnings Call');
  }

  if (props.data.type === 'segmentedByLabel') {
    return (
      <WrappedMarkdown.FC markdownText={props.data.displayName} />
    );
  }

  return props.data.displayName;
}

function __getContent(messageData: MessageData) {
  if (messageData.type === 'earningsCall' || messageData.type === 'deepDive') {
    return null;
  }

  const data = messageData.values.filter((dataPoint) => !dataPoint.period.includes('ytd')
    && !dataPoint.period.includes('ttm')
    && !dataPoint.period.includes('fy'))
    .map((dataPoint) => ({
      rate: Big(dataPoint.value)
        .toFixed(2),
      name: `${dataPoint.period}-${dataPoint.year}`
    }));

  const dataValues = data.map((d) => Number(d.rate));
  const dataMin = Math.min(...dataValues);
  const dataMax = Math.max(...dataValues);

  const generateDomain = (dataMin: number, dataMax: number): [number, number] => {
    let minDomain;
    let maxDomain;
    if (dataMin >= 0) {
      minDomain = 0;
    } else if (dataMin > -10) {
      minDomain = Math.floor(dataMin * 1.1 / 0.005) * 0.005;
    } else {
      minDomain = Math.floor(dataMin * 1.1);
    }

    if (dataMax <= 0) {
      maxDomain = 0;
    } else if (dataMax < 10) {
      maxDomain = Math.ceil(dataMax * 1.1 / 0.005) * 0.005;
    } else {
      maxDomain = Math.ceil(dataMax * 1.1);
    }

    return [
      minDomain,
      maxDomain
    ];
  };

  const generateTicks = (dataMin: number, dataMax: number): number[] => {
    if (dataMax >= 0 && dataMin >= 0) {
      const step = dataMax / 4;
      const rawTicks = [
        0,
        step,
        step * 2,
        step * 3,
        step * 4
      ];

      if (dataMax < 10 && dataMin < 10) {
        return rawTicks.map((t) => Math.round(t * 1000) / 1000);
      } else {
        return rawTicks.map((t) => Math.round(t));
      }
    }

    if (dataMax < 0 && dataMin < 0) {
      const step = Math.abs(dataMin) / 4;
      const rawTicks = [
        -step * 4,
        -step * 3,
        -step * 2,
        -step,
        0
      ];

      if (dataMax > -10 && dataMin > -10) {
        return rawTicks.map((t) => Math.round(t * 1000) / 1000);
      } else {
        return rawTicks.map((t) => Math.round(t));
      }
    }

    if (dataMax >= 0 && dataMin < 0) {
      const positiveStep = dataMax / 2;
      const negativeStep = Math.abs(dataMin) / 2;
      const rawTicks = [
        -negativeStep * 2,
        -negativeStep,
        0,
        positiveStep,
        positiveStep * 2
      ];

      if (dataMax < 10 && dataMin > -10) {
        return rawTicks.map((t) => Math.round(t * 1000) / 1000);
      } else {
        return rawTicks.map((t) => Math.round(t));
      }
    }
    return [];
  };

  return (
    <>
      {(data && data.length > 1) && (
        <ResponsiveContainer
          height={500}
        >
          <AreaChart
            data={data}
          >
            <CartesianGrid strokeDasharray='3 3' />

            <defs>
              <linearGradient
                id='rate'
                x1='0'
                y1='-.5'
                x2='1'
                y2='1'
              >
                <stop
                  offset='0'
                  stopColor='#02C37E'
                  stopOpacity={0.8}
                />
                <stop
                  offset='95%'
                  stopColor='#82ca9d'
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>

            <Tooltip content={(
              <CustomToolTip.FC
                active={true}
                label='name'
                payload={data ?? []}
              />
            )}
            />

            <YAxis
              fontFamily='Lexend'
              tickFormatter={(value) => {
                if (_.isNumber(value)) {
                  return __formatNumber(Big(value));
                }

                return value;
              }}
              ticks={generateTicks(dataMin, dataMax)}
              domain={generateDomain(dataMin, dataMax)}
            />

            <Area
              type='monotone'
              dataKey='rate'
              stroke={theme.palette.primary.main}
              fillOpacity={1}
              fill='url(#rate)'
            />

            <XAxis
              fontFamily='Lexend'
              padding={{
                left: 0,
                right: 16
              }}
              tickMargin={4}
              interval='preserveEnd'
              dataKey='name'
            />
          </AreaChart>
        </ResponsiveContainer>
      )}

      <MessageDataTable.FC
        messageData={messageData}
      />
    </>
  );
}



function __formatNumber(num: Big) {
  const abs = num.abs();
  if (abs.gte(1_000_000_000_000)) {
    return num.div(1_000_000_000_000).toFixed(2) + ' T';
  }
  if (abs.gte(1_000_000_000)) {
    return num.div(1_000_000_000).toFixed(2) + ' B';
  }
  if (abs.gte(1_000_000)) {
    return num.div(1_000_000).toFixed(2) + ' M';
  }

  return num.toString();
}
